import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  ClipboardCheck, Building2, User, DollarSign, Package, FileText,
  CheckCircle2, ArrowLeft, Save, Pen, Info, FileSignature, ShieldCheck
} from 'lucide-react';
import { KYCChecklistPanel } from '../kyc/KYCChecklistPanel';

function ReviewSection({ icon: Icon, title, onEdit, children }) {
  return (
    <div className="border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b bg-muted/20">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#3b82f6]" />
          <h4 className="font-semibold text-sm">{title}</h4>
        </div>
        {onEdit && (
          <button type="button" onClick={onEdit} className="text-[#3b82f6] hover:text-blue-700 text-sm font-medium flex items-center gap-1" data-testid={`edit-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            <Pen className="h-3 w-3" /> Edit
          </button>
        )}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function ReviewField({ label, value }) {
  return (
    <div className="py-1">
      <span className="text-xs text-muted-foreground">{label}:</span>{' '}
      <span className="text-sm font-medium">{value || '\u2014'}</span>
    </div>
  );
}

function Step6Review({ application, onPrevious, onSubmit, saving, onSaveDraft, onGoToStep }) {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [kycReady, setKycReady] = useState(false);

  const getStepData = (num) => application.sections?.find(s => s.step_number === num)?.data || {};
  const step1 = getStepData(1);
  const step2 = getStepData(2);
  const step3 = getStepData(3);
  const step4 = getStepData(4);
  const step5 = getStepData(5);

  const pricingLabels = {
    zero_fee: 'Zero-Fee Program',
    flat_rate: 'Flat Rate Program',
    custom: 'Custom Program'
  };
  const pricingDetails = {
    zero_fee: '4.0% \u2013 Customer pays processing fee',
    flat_rate: '2.69% + $0.10 per transaction',
    custom: 'Variable \u2013 Based on card type'
  };

  const documents = step5.documents || {};
  const uploadedDocs = Object.entries(documents).filter(([, v]) => v?.status === 'uploaded' || v?.status === 'pending');

  const docLabels = {
    business_license: 'Business License',
    voided_check: 'Voided Check',
    driver_license_front: 'Driver License',
    driver_license_back: 'Driver License (Back)',
    business_exterior: 'Business Exterior',
    business_interior: 'Business Interior',
    utility_bill: 'Utility Bill'
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreedToTerms) return;
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#3b82f6] flex items-center justify-center shrink-0">
          <ClipboardCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold" data-testid="step6-title">Review & Submit Application</h3>
          <p className="text-sm text-muted-foreground">Please review all information before submitting</p>
        </div>
      </div>

      {/* Business Information */}
      <ReviewSection icon={Building2} title="Business Information" onEdit={() => onGoToStep?.(1)}>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
          <ReviewField label="Legal Name" value={step1.legal_business_name} />
          <ReviewField label="Entity Type" value={step1.business_type?.replace(/_/g, ' ')} />
          <ReviewField label="Tax ID" value={step1.federal_tax_id} />
          <ReviewField label="Founded" value={step1.date_established} />
          <ReviewField label="Phone" value={step1.business_phone} />
          <ReviewField label="Email" value={step1.business_email} />
          <ReviewField label="Address" value={step1.business_address} />
          {step1.dba_same_as_corporate === 'no' && (
            <ReviewField label="DBA Name" value={step1.dba_name} />
          )}
          {step1.building_type && (
            <>
              <ReviewField label="Building Type" value={step1.building_type} />
              <ReviewField label="Ownership" value={step1.building_ownership} />
              <ReviewField label="Area Zoned" value={step1.area_zoned} />
              <ReviewField label="Square Footage" value={step1.square_footage} />
            </>
          )}
        </div>
      </ReviewSection>

      {/* Principal Information */}
      <ReviewSection icon={User} title="Principal Information" onEdit={() => onGoToStep?.(2)}>
        {step2.owners?.length > 0 ? step2.owners.map((owner, i) => (
          <div key={i} className={`${i > 0 ? 'mt-3 pt-3 border-t' : ''}`}>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
              <ReviewField label="Name" value={[owner.first_name, owner.middle_name, owner.last_name].filter(Boolean).join(' ')} />
              <ReviewField label="Title" value={owner.title} />
              <ReviewField label="Ownership" value={owner.ownership_percent ? `${owner.ownership_percent}%` : null} />
              <ReviewField label="Phone" value={owner.mobile_number} />
              <ReviewField label="Address" value={owner.home_address} />
            </div>
          </div>
        )) : <p className="text-sm text-muted-foreground">No owner information provided</p>}
      </ReviewSection>

      {/* Pricing Program */}
      <ReviewSection icon={DollarSign} title="Pricing Program" onEdit={() => onGoToStep?.(3)}>
        {step3.pricing_program ? (
          <div className="border-2 border-[#3b82f6] rounded-lg p-3 flex items-center justify-between bg-blue-50/30">
            <div>
              <div className="font-semibold text-sm">{pricingLabels[step3.pricing_program] || step3.pricing_program}</div>
              <div className="text-xs text-muted-foreground">{pricingDetails[step3.pricing_program]}</div>
            </div>
            <Badge className="bg-[#3b82f6] text-white">Selected</Badge>
          </div>
        ) : <p className="text-sm text-muted-foreground">No pricing program selected</p>}
        {step4.ftp_override && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 font-medium">
            Overridden to FTP Cash Discount at 5% (Free Placement selected)
          </div>
        )}
      </ReviewSection>

      {/* Equipment Selection */}
      <ReviewSection icon={Package} title="Equipment Selection" onEdit={() => onGoToStep?.(4)}>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
          <ReviewField label="Device" value={step4.device_type?.replace(/_/g, ' ')} />
          <ReviewField label="Software" value={step4.software_platform?.charAt(0).toUpperCase() + step4.software_platform?.slice(1)} />
          <ReviewField label="Terminal" value={step4.starter_kit?.replace(/_/g, ' ')} />
          <ReviewField label="Billing" value={step4.billing_responsibility?.replace(/_/g, ' ')} />
        </div>
      </ReviewSection>

      {/* Uploaded Documents */}
      <ReviewSection icon={FileText} title="Uploaded Documents" onEdit={() => onGoToStep?.(5)}>
        {uploadedDocs.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-2">
            {uploadedDocs.map(([key]) => (
              <div key={key} className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                <span className="text-sm font-medium">{docLabels[key] || key.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">No documents uploaded</p>}
      </ReviewSection>

      {/* KYC / Compliance Checklist */}
      <ReviewSection icon={ShieldCheck} title="KYC / Compliance">
        <KYCChecklistPanel applicationId={application.id} leadId={application.lead_id} onReady={setKycReady} />
      </ReviewSection>

      {/* Terms and Conditions */}
      <div className="border rounded-xl p-4 bg-amber-50/60 border-amber-200">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={agreedToTerms} onCheckedChange={(v) => setAgreedToTerms(!!v)} className="mt-1" data-testid="agree-terms" />
          <span className="text-sm text-amber-900">
            I acknowledge that I have read and agree to the{' '}
            <a href="#" className="text-[#3b82f6] underline font-medium">Terms and Conditions</a>,{' '}
            <a href="#" className="text-[#3b82f6] underline font-medium">Privacy Policy</a>, and{' '}
            <a href="#" className="text-[#3b82f6] underline font-medium">Merchant Agreement</a>.
            I understand that this application will be sent for digital signature and processing.
          </span>
        </label>
      </div>

      {/* Next Steps Info */}
      <div className="border rounded-xl p-4 bg-blue-50/40 border-blue-200">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-[#3b82f6] mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold text-sm">Next Steps:</span>{' '}
            <span className="text-sm text-muted-foreground">
              After submission, you will receive an email with a secure link to digitally sign your merchant agreement.
              Processing typically takes 1-2 business days.
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button type="button" variant="outline" onClick={() => onSaveDraft?.({})} data-testid="save-draft-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Documents
        </Button>
        <Button type="submit" disabled={saving || !agreedToTerms || !kycReady} className="bg-gray-700 hover:bg-gray-800 disabled:opacity-50" data-testid="submit-application-btn">
          <FileSignature className="h-4 w-4 mr-2" />
          {saving ? 'Submitting...' : !kycReady ? 'Complete KYC to Submit' : 'Submit Application for Signature'}
        </Button>
      </div>
    </form>
  );
}

export default Step6Review;
